'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';
import { TIMERS } from './constants';
import { getTimezone } from '@/utils/reward-machine/get-timezone';
import { useStore } from '@/store/store';
import { getServerTime } from '@/lib/aggregation';

const changeTimezone = (date: Date, ianaTimeZone: string) => {
    const invdate = new Date(
        date.toLocaleString('en-US', {
            timeZone: ianaTimeZone
        })
    );
    const diff = date.getTime() - invdate.getTime();

    return new Date(date.getTime() - diff);
};

const getHoursMinutesAndSeconds = (currentTime: Date): { hours: number; minutes: number; seconds: number } => {
    const tomorrowDate = new Date();
    const tomorrowDateMI = changeTimezone(tomorrowDate, getTimezone('MI') as string);
    tomorrowDateMI.setHours(TIMERS.hours, TIMERS.minutes, TIMERS.seconds);
    const tomorrowTimeMs = tomorrowDateMI.getTime();
    const currentTimeMs = currentTime.getTime();
    const timeDiff = tomorrowTimeMs - currentTimeMs;

    if (timeDiff < 0) {
        tomorrowDateMI.setDate(currentTime.getDate() + 1);
    }

    let hoursDiff = Math.floor(Math.abs(currentTimeMs - tomorrowTimeMs) / 36e5);
    let minDiff = Math.ceil(Math.abs((currentTimeMs - tomorrowTimeMs) % 36e5) / 6e4);
    const secDiff = Math.ceil(Math.floor(Math.abs((currentTimeMs - tomorrowTimeMs) % 36e5) % 6e4) / 1e3);

    if (minDiff === 60) {
        minDiff = 0;
        hoursDiff += 1;
    }
    const hours = hoursDiff;
    const minutes = minDiff;
    const seconds = secDiff === 0 ? 60 : secDiff;
    return { hours, minutes, seconds };
};

interface TimeLeftUntilTomorrowProps {
    shortLabels?: boolean;
    customServerTime?: string;
}

export const TimeLeftUntilTomorrowComponent = ({
    shortLabels = false,
    customServerTime
}: TimeLeftUntilTomorrowProps) => {
    const [timer, setTimer] = useState<number | null>(null);
    const initialTime = useMemo(
        () =>
            customServerTime
                ? getHoursMinutesAndSeconds(changeTimezone(new Date(customServerTime), getTimezone('MI') as string))
                : { hours: 0, minutes: 0, seconds: 0 },
        [customServerTime]
    );
    const [hours, setHours] = useState(initialTime.hours);
    const [minutes, setMinutes] = useState(initialTime.minutes);
    const [seconds, setSeconds] = useState(initialTime.seconds);
    const region = useStore((s) => s.regionInfo.currentRegion.region);
    const [serverTime, setServerTime] = useState<string>(() => new Date().toISOString());

    useEffect(() => {
        if (!customServerTime) {
            const timezone = getTimezone(region);
            if (!timezone) {
                return;
            }

            getServerTime(timezone).then((time) => {
                setServerTime(time);
            });
        } else {
            setServerTime(customServerTime);
        }
    }, [region, customServerTime]);

    const [currentTime, setCurrentTime] = useState<Date | null>(null);

    const timeLeftUntilTomorrow = useCallback(() => {
        if (currentTime) {
            const { hours: h, minutes: m, seconds: s } = getHoursMinutesAndSeconds(currentTime);
            setHours(h);
            setMinutes(m);
            setSeconds(s);
            const newCurrentTime = currentTime;
            newCurrentTime.setSeconds(0);
            newCurrentTime.setMinutes(currentTime.getMinutes() + 1);
            setCurrentTime(newCurrentTime);
        }
    }, [currentTime]);

    const updateTimer = () => {
        if (timer !== 0) {
            clearTimeout(timer as any);
        }
        timeLeftUntilTomorrow();
    };

    useEffect(() => {
        if (serverTime) {
            setCurrentTime(changeTimezone(new Date(serverTime), getTimezone('MI') as string));
        }
    }, [serverTime]);

    useEffect(() => {
        if (currentTime) {
            updateTimer();
        }
    }, [currentTime]);

    useEffect(() => {
        if (seconds > 0) {
            const newTimer = setTimeout(() => timeLeftUntilTomorrow(), seconds * 1e3);
            setTimer(newTimer as unknown as number);
        }
    }, [seconds, minutes]);

    return (
        <>
            {!shortLabels && (
                <span>
                    {!!hours && `${hours} ${hours === 1 ? 'hour' : 'hours'}`}{' '}
                    {!!minutes && `${minutes} ${minutes === 1 ? 'min' : 'mins'}`}
                </span>
            )}

            {shortLabels && (
                <span>
                    {!!hours && `${hours}h`} {!!minutes && `${minutes}m`}
                </span>
            )}
        </>
    );
};
